import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Activity, Mail, Shield, User } from 'lucide-react';
import InsuranceSection from '../components/InsuranceSection';
import { apiFetch } from '../utils/api';

/** Purpose: Executes format date. Inputs: value. Output: value. */
function formatDate(value) {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleString();
}

export default function Account({
  user,
  emailVerified,
  auditLogs = [],
  onUpdateProfile,
  onChangePassword,
  onResendVerification
}) {
  const [connectedEmails, setConnectedEmails] = useState([]);
  const [connectedEmailLoading, setConnectedEmailLoading] = useState(false);
  const [connectedEmailError, setConnectedEmailError] = useState('');

  const loadConnectedEmails = useCallback(async () => {
    setConnectedEmailLoading(true);
    setConnectedEmailError('');
    try {
      const response = await apiFetch('/connected-email');
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Failed to load connected emails');
      }
      const data = await response.json();
      setConnectedEmails(data.data || []);
    } catch (err) {
      console.error('Connected email load error:', err);
      setConnectedEmailError(err.message || 'Failed to load connected emails');
    } finally {
      setConnectedEmailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnectedEmails();
  }, [loadConnectedEmails]);

  const handleConnectGmail = useCallback(async () => {
    setConnectedEmailError('');
    try {
      const response = await apiFetch('/connected-email/google/start', {
        method: 'POST',
        body: JSON.stringify({ appRedirect: '/?view=account' })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Failed to start Gmail connect');
      }
      const data = await response.json();
      if (!data?.url) throw new Error('Missing Google OAuth URL');
      window.location.assign(data.url);
    } catch (err) {
      console.error('Connect Gmail error:', err);
      setConnectedEmailError(err.message || 'Failed to start Gmail connect');
    }
  }, []);

  const handleDisconnect = useCallback(async (id) => {
    setConnectedEmailError('');
    try {
      const response = await apiFetch(`/connected-email/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Failed to disconnect');
      }
      await loadConnectedEmails();
    } catch (err) {
      console.error('Disconnect email error:', err);
      setConnectedEmailError(err.message || 'Failed to disconnect');
    }
  }, [loadConnectedEmails]);

  const recentActivity = useMemo(() => {
    return [...auditLogs]
      .filter((log) => String(log.action || '').startsWith('AUTH_') || String(log.action || '').startsWith('SESSION_'))
      .slice(0, 8);
  }, [auditLogs]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Account</h2>
        <p className="text-sm text-slate-500 mt-1">Manage profile, verification, security settings, and recent activity.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <User className="w-5 h-5 text-slate-600" />
            Profile
          </h3>
          <div className="mt-4 space-y-2 text-sm">
            <p className="text-slate-700"><span className="font-medium">Name:</span> {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Not set'}</p>
            <p className="text-slate-700"><span className="font-medium">Email:</span> {user?.email || 'Not set'}</p>
            <p className="text-slate-700"><span className="font-medium">Phone:</span> {user?.phone || 'Not set'}</p>
            <p className="text-slate-700"><span className="font-medium">Birthdate:</span> {user?.birthDate ? new Date(user.birthDate).toLocaleDateString() : 'Not set'}</p>
            <p className="text-slate-700"><span className="font-medium">Address:</span> {[user?.addressLine1, user?.addressLine2, user?.city, user?.state, user?.postalCode].filter(Boolean).join(', ') || 'Not set'}</p>
          </div>
          <button
            onClick={onUpdateProfile}
            className="mt-4 w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Update Profile
          </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-slate-600" />
            Security
          </h3>
          <div className="mt-4 space-y-3 text-sm">
            <p className="text-slate-700">
              <span className="font-medium">Email verification:</span>{' '}
              <span className={emailVerified ? 'text-green-700' : 'text-amber-700'}>
                {emailVerified ? 'Verified' : 'Pending verification'}
              </span>
            </p>
            {!emailVerified && (
              <button
                onClick={onResendVerification}
                className="w-full sm:w-auto px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Resend Verification Email
              </button>
            )}
            <div>
              <button
                onClick={onChangePassword}
                className="w-full sm:w-auto px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800"
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      </div>

      <InsuranceSection />

      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Mail className="w-5 h-5 text-slate-600" />
          Connected Email
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          Connect Gmail to create drafts you can send from the email address the provider has on file.
        </p>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleConnectGmail}
            disabled={connectedEmailLoading}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
          >
            {connectedEmailLoading ? 'Loading…' : 'Connect Gmail'}
          </button>
          <button
            onClick={loadConnectedEmails}
            disabled={connectedEmailLoading}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            Refresh
          </button>
        </div>

        {connectedEmailError && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            {connectedEmailError}
          </div>
        )}

        <div className="mt-4 space-y-2">
          {connectedEmails.length === 0 ? (
            <p className="text-sm text-slate-500">No connected inboxes yet.</p>
          ) : (
            connectedEmails.map((acct) => (
              <div key={acct.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-slate-200 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{acct.email}</p>
                  <p className="text-xs text-slate-500">{acct.provider}</p>
                </div>
                <button
                  onClick={() => handleDisconnect(acct.id)}
                  className="w-full sm:w-auto px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg text-xs hover:bg-slate-50"
                >
                  Disconnect
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-slate-600" />
          Recent Account Activity
        </h3>
        <div className="mt-4 space-y-2">
          {recentActivity.length === 0 ? (
            <p className="text-sm text-slate-500">No recent session or auth events found.</p>
          ) : (
            recentActivity.map((log) => (
              <div key={log.id} className="border border-slate-200 rounded-lg px-3 py-2">
                <p className="text-sm font-medium text-slate-900">{String(log.action || '').replace(/_/g, ' ')}</p>
                <p className="text-xs text-slate-500">{formatDate(log.timestamp)}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
